import { useEffect, useRef, useState } from 'react';
import { NavLink, Link, Outlet, useLocation } from 'react-router-dom';
import { ShoppingCart, Moon, Sun, Menu, X } from 'lucide-react';
import { useCart, cartCount } from '@/lib/cartStore';
import { useShopTheme } from '@/hooks/useShopTheme';
import { useHydrated } from '@/hooks/useBrowserOnly';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { EvieOrb } from '@/components/EvieOrb';
import { EvieSheet } from '@/components/EvieSheet';
import { ScrollToTop } from '@/components/ScrollToTop';
import { InstallerBand } from '@/components/InstallerBand';
import { HOSTS_URL, PARTNERS_URL, PRICING_URL } from '@/lib/partnerLinks';
import styles from './ShopLayout.module.css';

/** Menu order is locked: Commercial DC, Commercial AC, Home Charging,
 *  Accessories. DC leads. */
const NAV = [
  { to: '/commercial-dc', label: 'Commercial DC' },
  { to: '/commercial-ac', label: 'Commercial AC' },
  { to: '/home-charging', label: 'Home Charging' },
  { to: '/accessories', label: 'Accessories' },
] as const;

export function ShopLayout() {
  useDocumentHead();
  // The cart and the theme live in this browser, so the server cannot know them.
  // The first render matches the server's markup (empty cart, dark), then corrects.
  const hydrated = useHydrated();
  const storedCount = useCart((s) => cartCount(s.lines));
  const count = hydrated ? storedCount : 0;
  const { theme: storedTheme, toggle } = useShopTheme();
  const theme = hydrated ? storedTheme : 'dark';
  // EVII lives at the layout level so the conversation survives route changes
  // (ShopLayout stays mounted across navigation). Header orb toggles the sheet.
  const [evieOpen, setEvieOpen] = useState(false);
  // Mobile menu (Warren, 9 Oct 2026): below 720px the nav is hidden, so a
  // button opens a full-width panel. It is open only for the path it was
  // opened on, so any navigation closes it; Escape, the button and a link
  // close it too, and the page behind does not scroll while it is open.
  const { pathname } = useLocation();
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const menuOpen = menuFor === pathname;
  const closeMenu = () => setMenuFor(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    menuRef.current?.querySelector('a')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenuFor(null);
      menuBtnRef.current?.focus();
    };
    // Widening past the breakpoint hides the button, so the menu goes too.
    const wide = window.matchMedia('(min-width: 721px)');
    const onWide = () => {
      if (wide.matches) setMenuFor(null);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    wide.addEventListener('change', onWide);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      wide.removeEventListener('change', onWide);
    };
  }, [menuOpen]);
  return (
    <>
      <ScrollToTop />
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.brand} aria-label="EV Initiative store home">
            <img className={styles.brandMark} src="/evi-logo-mark.png" alt="" aria-hidden width={22} height={22} />
            <span className={styles.brandWord}>EV INITIATIVE</span>
          </Link>

          <nav className={styles.nav} aria-label="Shop categories">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink
                }
              >
                {item.label}
              </NavLink>
            ))}
            {/* Entry point for installers (Warren, 8 Oct 2026): the program
                lives on evinitiative.com, same tab. */}
            <a className={styles.navLink} href={PARTNERS_URL}>For installers</a>
          </nav>

          <button
            type="button"
            className={styles.evieBtn}
            onClick={() => setEvieOpen(true)}
            aria-label="Ask EVII™"
          >
            <EvieOrb size={24} />
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={toggle}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <Link to="/cart" className={styles.cartBtn} aria-label={`Cart, ${count} item${count === 1 ? '' : 's'}`}>
            <ShoppingCart size={20} />
            {count > 0 && <span className={styles.cartBadge}>{count}</span>}
          </Link>
          <button
            ref={menuBtnRef}
            type="button"
            className={styles.menuBtn}
            onClick={() => setMenuFor(menuOpen ? null : pathname)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="shop-menu"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {menuOpen && (
          <nav id="shop-menu" ref={menuRef} className={styles.menuPanel} aria-label="Menu">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={closeMenu}
                className={({ isActive }) =>
                  isActive ? `${styles.menuLink} ${styles.menuLinkActive}` : styles.menuLink
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a className={styles.menuLink} href={PARTNERS_URL} onClick={closeMenu}>For installers</a>
            <NavLink
              to="/cart"
              onClick={closeMenu}
              className={({ isActive }) =>
                isActive ? `${styles.menuLink} ${styles.menuLinkActive}` : styles.menuLink
              }
            >
              <ShoppingCart size={18} aria-hidden />
              Cart{count > 0 ? ` (${count})` : ''}
            </NavLink>
          </nav>
        )}
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>

      <EvieSheet isOpen={evieOpen} onClose={() => setEvieOpen(false)} />

      <InstallerBand className={styles.installerBand} />

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          {/* Proper footer menu (Warren): three labeled columns so the
              Sales Partner + Incentives pages are findable from every page,
              not just their inline CTAs. Legal links and the Sales Partner
              program stay external: their canonical pages live on
              evinitiative.com. */}
          <div className={styles.footerCols}>
            <nav className={styles.footerCol} aria-label="Shop">
              <p className={styles.footerColTitle}>Shop</p>
              {NAV.map((item) => (
                <Link key={item.to} to={item.to} className={styles.footerLink}>
                  {item.label}
                </Link>
              ))}
            </nav>
            <nav className={styles.footerCol} aria-label="Programs">
              <p className={styles.footerColTitle}>Programs</p>
              <a href={PARTNERS_URL} className={styles.footerLink}>Become an EV Initiative Sales Partner</a>
              <a href={HOSTS_URL} className={styles.footerLink}>Host EV charging at your property</a>
              <a href={PRICING_URL} className={styles.footerLink}>EV charging network pricing</a>
              <Link to="/incentives" className={styles.footerLink}>Incentives &amp; Rebates</Link>
            </nav>
            <nav className={styles.footerCol} aria-label="Legal">
              <p className={styles.footerColTitle}>Legal</p>
              <a className={styles.footerLink} href="https://evinitiative.com/privacy">Privacy Policy</a>
              <a className={styles.footerLink} href="https://evinitiative.com/california-privacy">California Privacy Notice</a>
              <a className={styles.footerLink} href="https://evinitiative.com/terms">Terms of Use</a>
              <a className={styles.footerLink} href="https://evinitiative.com/sales-partner-terms">Sales Partner Terms</a>
            </nav>
          </div>
          <div className={styles.footerMeta}>
            <span>&copy; {'2026'} EV Initiative</span>
          </div>
        </div>
      </footer>
    </>
  );
}
